/* ================================================================
   STORE LOCATOR — section-store-locator.js

   Two data paths:

   1. ProMap (live). The RP ProMap app's script tags run on every page
      of the shop and define `window.SCASLSetting`. Its `locationsRaw`
      is a JSON string holding every location the merchant maintains in
      the app admin. We parse that and build OUR markup from it, so the
      page keeps the approved design instead of inheriting the app's
      own widget DOM — and it survives the app restyling its widget.

   2. Theme blocks. What Liquid rendered server-side. Used when the app
      is absent (theme editor, app uninstalled, script blocked) so the
      page is never empty.

   Filtering, the tag chips and the map callout then work the same way
   whichever path supplied the cards.
   ================================================================ */
(function () {
  'use strict';

  var PROMAP_TIMEOUT_MS = 8000;
  var PROMAP_POLL_MS = 120;
  var SVGNS = 'http://www.w3.org/2000/svg';
  var XLINKNS = 'http://www.w3.org/1999/xlink';

  var norm = function (v) {
    return (v || '').toString().toLowerCase().replace(/\s+/g, ' ').trim();
  };

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function icon(cls, id) {
    var svg = document.createElementNS(SVGNS, 'svg');
    svg.setAttribute('class', cls);
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    var use = document.createElementNS(SVGNS, 'use');
    use.setAttribute('href', '#' + id);
    /* Safari < 16 still wants the xlink form. */
    use.setAttributeNS(XLINKNS, 'xlink:href', '#' + id);
    svg.appendChild(use);
    return svg;
  }

  /* ProMap stores US states as two-letter codes, which read badly as
     "Stores by State" pills. Anything not in this table (Guangdong, New
     South Wales) is passed through untouched. */
  var STATE_NAMES = {
    AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
    CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'Washington DC',
    FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
    IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
    ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan',
    MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri', MT: 'Montana',
    NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
    NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota',
    OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania',
    RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota',
    TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia',
    WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
    PR: 'Puerto Rico'
  };

  function stateName(v) {
    var raw = (v || '').trim();
    return STATE_NAMES[raw.toUpperCase()] || raw;
  }

  /* ---------------------------------------------------------------
     ProMap adapter
     --------------------------------------------------------------- */

  /* ProMap stores a single-line address ("719A Main Street Pleasanton,
     CA 94566") alongside separate city/state/postal fields, while the
     design wants street on line 1 and locality on line 2. Strip the
     locality back off line 1 when it is there, and leave it alone when
     it is not — plenty of records are already just the street. */
  function splitAddress(loc) {
    var street = (loc.address || '').trim();
    var city = (loc.city || '').trim();
    var state = (loc.state || '').trim();
    var postal = (loc.postal || loc.zipcode || '').trim();

    var left = [city, state].filter(Boolean).join(', ');
    var locality = [left, postal].filter(Boolean).join(' ').trim();

    if (locality) {
      var tail = new RegExp('[,\\s]*' + locality.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '$', 'i');
      street = street.replace(tail, '').replace(/[,\s]+$/, '').trim();
    }
    if (!street) street = (loc.address || '').trim();
    if (loc.address2) street += (street ? ', ' : '') + String(loc.address2).trim();

    return { street: street, locality: locality };
  }

  /* The button says "Directions", so prefer a routing URL. ProMap's own
     shareable_link is a Google *place* link, which opens the listing
     rather than starting navigation — keep it only as a last resort. */
  function directionsHref(loc) {
    if (loc.lat && loc.lng) {
      return 'https://www.google.com/maps/dir/?api=1&destination=' +
        encodeURIComponent(loc.lat + ',' + loc.lng);
    }
    if (loc.shareable_link) return loc.shareable_link;
    var parts = [loc.name, loc.address, loc.city, loc.state, loc.postal].filter(Boolean);
    return 'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(parts.join(', '));
  }

  function readProMap() {
    var setting = window.SCASLSetting;
    if (!setting || !setting.locationsRaw) return null;
    var raw;
    try {
      raw = typeof setting.locationsRaw === 'string'
        ? JSON.parse(setting.locationsRaw)
        : setting.locationsRaw;
    } catch (e) {
      return null;
    }
    if (!raw || !raw.length) return null;

    return raw.map(function (loc) {
      var a = splitAddress(loc);
      var tag = (loc.tagsvalue || '').split(',')[0].trim();
      if (!tag && loc.tags && loc.tags.length && loc.tags[0].tag) tag = String(loc.tags[0].tag).trim();
      return {
        id: loc.id,
        name: (loc.name || loc.title || '').trim(),
        street: a.street,
        locality: a.locality,
        phone: (loc.phone || '').trim(),
        tag: tag,
        city: (loc.city || '').trim(),
        state: stateName(loc.state),
        country: (loc.country || '').trim(),
        lat: parseFloat(loc.lat),
        lng: parseFloat(loc.lng),
        href: directionsHref(loc)
      };
    }).filter(function (s) { return s.name; });
  }

  function whenProMapReady(cb) {
    var waited = 0;
    (function poll() {
      var data = readProMap();
      if (data) return cb(data);
      waited += PROMAP_POLL_MS;
      if (waited >= PROMAP_TIMEOUT_MS) return cb(null);
      window.setTimeout(poll, PROMAP_POLL_MS);
    })();
  }

  /* ---------------------------------------------------------------
     Rendering — mirrors the Liquid markup exactly
     --------------------------------------------------------------- */

  function buildCard(store, labels, groupBy) {
    var card = el('article', 'sl-card');
    card.setAttribute('data-sl-card', '');
    card.setAttribute('data-name', store.name);
    card.setAttribute('data-address', store.street);
    card.setAttribute('data-city', store.locality);
    card.setAttribute('data-tag', store.tag);
    card.setAttribute('data-group', store[groupBy] || store.tag || '');
    card.setAttribute('data-country', store.country || '');
    if (isFinite(store.lat) && isFinite(store.lng)) {
      card.setAttribute('data-lat', store.lat);
      card.setAttribute('data-lng', store.lng);
    }

    var head = el('div', 'sl-card__head');
    head.appendChild(icon('sl-card__pin', 'sl-i-pin'));

    var body = el('div', 'sl-card__body');
    body.appendChild(el('h2', 'sl-card__name', store.name));

    var addr = el('p', 'sl-card__address');
    addr.appendChild(document.createTextNode(store.street));
    if (store.locality) {
      addr.appendChild(document.createElement('br'));
      addr.appendChild(document.createTextNode(store.locality));
    }
    body.appendChild(addr);

    if (store.phone) {
      var tel = el('a', 'sl-card__phone');
      tel.href = 'tel:' + store.phone.replace(/[^\d+]/g, '');
      tel.appendChild(icon('sl-card__phone-icon', 'sl-i-phone'));
      tel.appendChild(el('span', null, store.phone));
      body.appendChild(tel);
    }

    var actions = el('div', 'sl-card__actions');
    var dir = el('a', 'sl-btn sl-btn--directions');
    dir.href = store.href;
    dir.target = '_blank';
    dir.rel = 'noopener';
    dir.appendChild(icon('sl-btn__icon', 'sl-i-arrow'));
    dir.appendChild(el('span', null, labels.directions));
    actions.appendChild(dir);

    var view = el('button', 'sl-btn sl-btn--map', labels.view);
    view.type = 'button';
    view.setAttribute('data-sl-view', '');
    actions.appendChild(view);

    body.appendChild(actions);
    head.appendChild(body);
    card.appendChild(head);
    return card;
  }

  function hydrate(root, stores) {
    var list = root.querySelector('[data-sl-list]');
    if (!list) return false;

    var labels = {
      directions: list.dataset.directionsLabel || 'Directions',
      view: list.dataset.viewLabel || 'View on Map'
    };

    var groupBy = root.dataset.slGroupBy || 'tag';
    if (['tag', 'city', 'state'].indexOf(groupBy) === -1) groupBy = 'tag';

    var keep = list.querySelector('[data-sl-empty]');
    var loading = list.querySelector('[data-sl-loading]');
    var frag = document.createDocumentFragment();
    stores.forEach(function (store, i) {
      var card = buildCard(store, labels, groupBy);
      if (i === 0) card.classList.add('is-active');
      frag.appendChild(card);
    });
    list.textContent = '';
    list.appendChild(frag);
    if (keep) list.appendChild(keep);
    if (loading) loading.remove();

    /* Both pill sets are derived, so they can never disagree with the list.
       Overseas stockists are split out: grouping them by state would print
       Guangdong and New South Wales next to the US states. */
    var splitCountries = root.dataset.slSplitCountries === '1';
    var home = norm(root.dataset.slHomeCountry || 'USA');

    var primaryStores = stores;
    var overseas = [];
    if (splitCountries && home) {
      primaryStores = [];
      stores.forEach(function (s) {
        if (norm(s.country) && norm(s.country) !== home) overseas.push(s);
        else primaryStores.push(s);
      });
    }

    function tally(rows, field) {
      var counts = {};
      rows.forEach(function (r) {
        var key = (r[field] || '').trim();
        if (!key) return;
        counts[key] = (counts[key] || 0) + 1;
      });
      return counts;
    }

    function paint(listEl, counts, chipAttr) {
      if (!listEl) return [];
      var keys = Object.keys(counts).sort(function (a, b) { return a.localeCompare(b); });
      listEl.textContent = '';
      keys.forEach(function (key) {
        var li = document.createElement('li');
        var btn = el('button', 'sl-state', key + ' (' + counts[key] + ')');
        btn.type = 'button';
        btn.setAttribute(chipAttr, key);
        li.appendChild(btn);
        listEl.appendChild(li);
      });
      var panel = listEl.closest('.sl-states');
      /* Each shell ships hidden because it has nothing to show until the app
         answers; reveal it only once there is something in it. */
      if (panel) panel.hidden = keys.length === 0;
      return keys;
    }

    var primaryCounts = tally(primaryStores, groupBy);
    var tags = paint(root.querySelector('[data-sl-chips]'), primaryCounts, 'data-sl-tag-chip');
    paint(root.querySelector('[data-sl-country-chips]'), tally(overseas, 'country'), 'data-sl-country-chip');

    var select = root.querySelector('[data-sl-tag]');
    if (select) {
      var all = select.querySelector('option[value=""]');
      select.textContent = '';
      if (all) select.appendChild(all);
      tags.forEach(function (tag) {
        var opt = document.createElement('option');
        opt.value = tag;
        opt.textContent = tag;
        select.appendChild(opt);
      });
    }

    root.dataset.slHydrated = 'promap';
    return true;
  }

  /* ---------------------------------------------------------------
     Live map

     The Google Maps key comes from ProMap's own settings payload, so the
     map is on the merchant's existing key and needs no extra config. The
     static artwork stays underneath as the poster frame.
     --------------------------------------------------------------- */

  var mapsPending = [];
  var mapsRequested = false;

  function loadGoogleMaps(key, cb) {
    if (window.google && window.google.maps && window.google.maps.Map) return cb();
    mapsPending.push(cb);
    if (mapsRequested) return;
    mapsRequested = true;
    window.__slMapsReady = function () {
      mapsPending.splice(0).forEach(function (fn) { fn(); });
    };
    var tag = document.createElement('script');
    tag.src = 'https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(key) +
      '&loading=async&callback=__slMapsReady';
    tag.async = true;
    tag.onerror = function () { mapsPending.length = 0; };
    document.head.appendChild(tag);
  }

  function pinIcon() {
    var svg = document.querySelector('#sl-i-pin-lg');
    /* Re-use the callout's pin so the markers are the same kawaii shape. */
    var path = svg ? svg.querySelector('path').getAttribute('d') : '';
    var markup = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 25.083 36.667" width="25" height="37">' +
      '<path fill="#FF89B3" fill-rule="evenodd" d="' + path + '"/></svg>';
    return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(markup);
  }

  function setupMap(root, onPick) {
    var host = root.querySelector('[data-sl-map]');
    var panel = root.querySelector('.sl-map');
    if (!host || !panel) return null;

    var setting = window.SCASLSetting || {};
    var key = setting.apikey;
    if (!key) return null;

    var focusZoom = parseInt(host.dataset.focusZoom, 10) || 12;
    var controls = host.dataset.controls === '1';
    var api = null;

    loadGoogleMaps(key, function () {
      var g = window.google.maps;
      var map = new g.Map(host, {
        center: {
          lat: parseFloat(setting.default_latitude) || 37.0902,
          lng: parseFloat(setting.default_longitude) || -95.7129
        },
        zoom: parseInt(setting.default_view_zoom, 10) || 4,
        zoomControl: controls,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        clickableIcons: false,
        gestureHandling: 'cooperative'
      });

      var icon = { url: pinIcon(), scaledSize: new g.Size(25, 37), anchor: new g.Point(12, 37) };
      var markers = [];

      root.querySelectorAll('[data-sl-card]').forEach(function (card) {
        var lat = parseFloat(card.dataset.lat);
        var lng = parseFloat(card.dataset.lng);
        if (!isFinite(lat) || !isFinite(lng)) return;
        var marker = new g.Marker({
          position: { lat: lat, lng: lng },
          map: map,
          icon: icon,
          title: card.dataset.name || ''
        });
        marker.addListener('click', function () { onPick(card); });
        card._slMarker = marker;
        markers.push(marker);
      });

      function fitTo(cards) {
        var bounds = new g.LatLngBounds();
        var n = 0;
        cards.forEach(function (c) {
          if (!c._slMarker) return;
          bounds.extend(c._slMarker.getPosition());
          n += 1;
        });
        if (!n) return;
        if (n === 1) { map.setCenter(bounds.getCenter()); map.setZoom(focusZoom); return; }
        map.fitBounds(bounds, 48);
      }

      api = {
        focus: function (card) {
          if (!card || !card._slMarker) return;
          map.panTo(card._slMarker.getPosition());
          if (map.getZoom() < focusZoom) map.setZoom(focusZoom);
        },
        sync: function (visible) {
          var shown = {};
          visible.forEach(function (c) { shown[c.dataset.name + '|' + c.dataset.lat] = true; });
          root.querySelectorAll('[data-sl-card]').forEach(function (c) {
            if (!c._slMarker) return;
            c._slMarker.setMap(shown[c.dataset.name + '|' + c.dataset.lat] ? map : null);
          });
          fitTo(visible);
        }
      };
      panel.classList.add('is-live');
      root._slMap = api;
      if (root._slMapReady) root._slMapReady();
    });

    return { get ready() { return api; } };
  }

  /* ---------------------------------------------------------------
     Behaviour
     --------------------------------------------------------------- */

  function wire(root, signal) {
    var opts = signal ? { signal: signal } : undefined;
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-sl-card]'));
    if (!cards.length) return;

    var list = root.querySelector('[data-sl-list]');
    var searchInput = root.querySelector('[data-sl-search]');
    var tagSelect = root.querySelector('[data-sl-tag]');
    var countEl = root.querySelector('[data-sl-count]');
    var emptyEl = root.querySelector('[data-sl-empty]');
    var popup = root.querySelector('[data-sl-popup]');
    var popupName = root.querySelector('[data-sl-popup-name]');
    var popupAddress = root.querySelector('[data-sl-popup-address]');
    var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-sl-tab]'));
    var chips = Array.prototype.slice.call(root.querySelectorAll('[data-sl-tag-chip]'));
    var countryChips = Array.prototype.slice.call(root.querySelectorAll('[data-sl-country-chip]'));

    /* One filter is active at a time: either a primary pill / the dropdown
       (matched on data-group) or an overseas pill (matched on data-country).
       Holding it in one place keeps the two panels from fighting. */
    var filter = null;

    /* Everything a keyword can match, pre-computed once per card. */
    cards.forEach(function (card) {
      card._slHaystack = norm(
        [card.dataset.name, card.dataset.address, card.dataset.city,
         card.dataset.tag, card.dataset.group, card.dataset.country].join(' ')
      );
      card._slTag = norm(card.dataset.group || card.dataset.tag);
      card._slCountry = norm(card.dataset.country);
    });

    var template = countEl ? countEl.dataset.template || '[count] stores found' : '';
    var templateOne = countEl ? countEl.dataset.templateOne || template : '';

    function setCount(n) {
      if (!countEl) return;
      var t = n === 1 ? templateOne : template;
      countEl.textContent = t.replace('[count]', n);
    }

    function apply() {
      var q = norm(searchInput ? searchInput.value : '');
      /* The dropdown is the primary pill set's other face, so keep them in step. */
      var picked = norm(tagSelect ? tagSelect.value : '');
      if (picked) filter = { key: 'group', value: picked };
      else if (filter && filter.key === 'group') filter = null;

      var shown = 0;
      var firstVisible = null;

      cards.forEach(function (card) {
        var matched = true;
        if (filter) {
          matched = (filter.key === 'country' ? card._slCountry : card._slTag) === norm(filter.value);
        }
        var ok = (!q || card._slHaystack.indexOf(q) !== -1) && matched;
        card.hidden = !ok;
        if (ok) {
          shown += 1;
          if (!firstVisible) firstVisible = card;
        }
      });

      setCount(shown);
      if (emptyEl) emptyEl.hidden = shown !== 0;
      if (root._slMap) root._slMap.sync(cards.filter(function (c) { return !c.hidden; }));

      var onGroup = filter && filter.key === 'group' ? norm(filter.value) : '';
      var onCountry = filter && filter.key === 'country' ? norm(filter.value) : '';
      chips.forEach(function (chip) {
        chip.classList.toggle('is-active', !!onGroup && norm(chip.dataset.slTagChip) === onGroup);
      });
      countryChips.forEach(function (chip) {
        chip.classList.toggle('is-active', !!onCountry && norm(chip.dataset.slCountryChip) === onCountry);
      });

      /* Keep the active card inside the result set so the callout never
         describes a store the visitor can no longer see. Re-selecting it is
         housekeeping, not a user action, so the map is left where it is. */
      var active = root.querySelector('.sl-card.is-active');
      if (firstVisible && (!active || active.hidden)) activate(firstVisible, {});
    }

    function activate(card, opts) {
      opts = opts || {};
      cards.forEach(function (c) { c.classList.toggle('is-active', c === card); });

      if (popup && popupName && popupAddress) {
        popupName.textContent = card.dataset.name || '';
        popupAddress.textContent = '';
        popupAddress.appendChild(document.createTextNode(card.dataset.address || ''));
        if (card.dataset.city) {
          popupAddress.appendChild(document.createElement('br'));
          popupAddress.appendChild(document.createTextNode(card.dataset.city));
        }
      }

      if (opts.focusMap && root._slMap) root._slMap.focus(card);

      if (opts.scrollTo && popup && window.matchMedia('(max-width: 1023px)').matches) {
        popup.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    if (searchInput) {
      searchInput.addEventListener('input', apply, opts);
      searchInput.addEventListener('search', apply, opts);
    }
    if (tagSelect) tagSelect.addEventListener('change', apply, opts);

    chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        var value = chip.dataset.slTagChip;
        var already = filter && filter.key === 'group' && norm(filter.value) === norm(value);
        filter = already ? null : { key: 'group', value: value };
        if (tagSelect) tagSelect.value = already ? '' : value;
        selectTab(already ? 'browse' : 'state', false);
        apply();
        if (list) list.scrollTop = 0;
      }, opts);
    });

    countryChips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        var value = chip.dataset.slCountryChip;
        var already = filter && filter.key === 'country' && norm(filter.value) === norm(value);
        /* Clear the dropdown first: it only speaks for the primary pills, and
           apply() would otherwise read a stale selection back over this. */
        if (tagSelect) tagSelect.value = '';
        filter = already ? null : { key: 'country', value: value };
        selectTab(already ? 'browse' : 'state', false);
        apply();
        if (list) list.scrollTop = 0;
      }, opts);
    });

    if (list) {
      list.addEventListener('click', function (event) {
        var card = event.target.closest('[data-sl-card]');
        if (!card) return;
        /* Let the phone and Directions links do their own thing. */
        if (event.target.closest('a')) {
          activate(card, {});
          return;
        }
        var viaButton = !!event.target.closest('[data-sl-view]');
        activate(card, { focusMap: true, scrollTo: viaButton });
      }, opts);
    }

    function selectTab(mode, focus) {
      tabs.forEach(function (tab) {
        tab.setAttribute('aria-selected', tab.dataset.slTab === mode ? 'true' : 'false');
      });
      if (mode === 'browse') {
        if (searchInput) searchInput.value = '';
        if (tagSelect) tagSelect.value = '';
        filter = null;
      }
      if (!focus) return;
      if (mode === 'search' && searchInput) searchInput.focus();
      if (mode === 'state' && tagSelect) tagSelect.focus();
    }

    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        selectTab(tab.dataset.slTab, true);
        apply();
      }, opts);
    });

    /* Handles for the map: it mounts later and needs to drive the same
       selection logic the list uses. */
    root._slApply = apply;
    root._slActivate = activate;

    apply();
  }

  function init(root) {
    if (root.dataset.slReady === 'true') return;
    root.dataset.slReady = 'true';

    if (root.dataset.slSource === 'blocks') {
      wire(root);
      return;
    }

    var list = root.querySelector('[data-sl-list]');
    var loading = list ? list.querySelector('[data-sl-loading]') : null;

    /* The list can be legitimately empty here: in ProMap mode the template
       ships no stores, because inventing placeholder addresses would be
       worse than showing nothing if the app never answers. */
    function settle(gotData) {
      if (loading) loading.remove();
      if (gotData) return;
      var empty = root.querySelector('[data-sl-empty]');
      if (empty && !root.querySelector('[data-sl-card]')) empty.hidden = false;
    }

    /* Wire the server-rendered blocks straight away so the page is usable
       while ProMap's script is still in flight. Every listener is tied to an
       AbortController, so swapping in the live data tears the old wiring down
       instead of stacking a second set of handlers on the shared controls. */
    var controller = new AbortController();
    wire(root, controller.signal);

    whenProMapReady(function (stores) {
      if (!stores || !hydrate(root, stores)) {
        settle(false);
        return;
      }
      settle(true);
      controller.abort();
      wire(root, new AbortController().signal);

      if (root.dataset.slLiveMap === '1') {
        root._slMapReady = function () { if (root._slApply) root._slApply(); };
        setupMap(root, function (card) {
          if (root._slActivate) root._slActivate(card, { focusMap: true });
          card.scrollIntoView({ block: 'nearest' });
        });
      }
    });
  }

  function boot(scope) {
    (scope || document).querySelectorAll('[data-store-locator]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }

  /* Theme editor: re-run after a section or block is re-rendered. */
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('shopify:block:select', function (e) {
    var card = e.target.closest ? e.target.closest('[data-sl-card]') : null;
    if (card) card.scrollIntoView({ block: 'nearest' });
  });
})();
